/**
 * useOpdrachtSections Hook
 * Admin hook for managing opdracht sections (create, update, delete, reorder)
 */

import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { ChallengeSection, SectionType, SectionConfig } from "../types";

interface CreateSectionInput {
  title: string;
  description?: string;
  section_type: SectionType;
  config?: SectionConfig;
  is_required?: boolean;
  points?: number;
  time_limit_minutes?: number;
}

interface UpdateSectionInput {
  title?: string;
  description?: string;
  section_type?: SectionType;
  config?: SectionConfig;
  is_required?: boolean;
  points?: number;
  time_limit_minutes?: number;
}

interface UseOpdrachtSectionsReturn {
  /** Create a new section */
  createSection: (
    challengeId: string,
    input: CreateSectionInput
  ) => Promise<ChallengeSection | null>;
  /** Update a section */
  updateSection: (
    sectionId: string,
    input: UpdateSectionInput
  ) => Promise<boolean>;
  /** Delete a section */
  deleteSection: (sectionId: string) => Promise<boolean>;
  /** Reorder sections */
  reorderSections: (
    sectionIds: string[]
  ) => Promise<boolean>;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
}

/**
 * Hook for admin management of opdracht sections
 */
export const useOpdrachtSections = (): UseOpdrachtSectionsReturn => {
  const { activeProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a new section
   */
  const createSection = useCallback(
    async (
      challengeId: string,
      input: CreateSectionInput
    ): Promise<ChallengeSection | null> => {
      if (!activeProfile?.id) return null;

      setIsLoading(true);
      setError(null);

      try {
        // Get current max order_index
        const { data: existingSections } = await supabase
          .from("challenge_sections")
          .select("order_index")
          .eq("challenge_id", challengeId)
          .order("order_index", { ascending: false })
          .limit(1);

        const nextOrder =
          existingSections && existingSections.length > 0
            ? existingSections[0].order_index + 1
            : 0;

        const sectionData = {
          challenge_id: challengeId,
          title: input.title,
          description: input.description || null,
          section_type: input.section_type,
          config: input.config || {},
          order_index: nextOrder,
          is_required: input.is_required ?? true,
          points: input.points || 0,
          time_limit_minutes: input.time_limit_minutes || null,
        };

        const { data, error: insertError } = await supabase
          .from("challenge_sections")
          .insert(sectionData)
          .select()
          .single();

        if (insertError) throw insertError;

        return data as ChallengeSection;
      } catch (err: any) {
        console.error("Error creating section:", err);
        setError(err.message || "Kon sectie niet aanmaken");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [activeProfile?.id]
  );

  /**
   * Update a section
   */
  const updateSection = useCallback(
    async (sectionId: string, input: UpdateSectionInput): Promise<boolean> => {
      if (!activeProfile?.id) return false;

      setIsLoading(true);
      setError(null);

      try {
        const updates: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
        };

        if (input.title !== undefined) updates.title = input.title;
        if (input.description !== undefined) updates.description = input.description;
        if (input.section_type !== undefined) updates.section_type = input.section_type;
        if (input.config !== undefined) updates.config = input.config;
        if (input.is_required !== undefined) updates.is_required = input.is_required;
        if (input.points !== undefined) updates.points = input.points;
        if (input.time_limit_minutes !== undefined)
          updates.time_limit_minutes = input.time_limit_minutes;

        const { error: updateError } = await supabase
          .from("challenge_sections")
          .update(updates)
          .eq("id", sectionId);

        if (updateError) throw updateError;

        return true;
      } catch (err: any) {
        console.error("Error updating section:", err);
        setError(err.message || "Kon sectie niet bijwerken");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [activeProfile?.id]
  );

  /**
   * Delete a section
   */
  const deleteSection = useCallback(
    async (sectionId: string): Promise<boolean> => {
      if (!activeProfile?.id) return false;

      setIsLoading(true);
      setError(null);

      try {
        const { error: deleteError } = await supabase
          .from("challenge_sections")
          .delete()
          .eq("id", sectionId);

        if (deleteError) throw deleteError;

        return true;
      } catch (err: any) {
        console.error("Error deleting section:", err);
        setError(err.message || "Kon sectie niet verwijderen");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [activeProfile?.id]
  );

  /**
   * Reorder sections
   */
  const reorderSections = useCallback(
    async (sectionIds: string[]): Promise<boolean> => {
      if (!activeProfile?.id) return false;

      setIsLoading(true);
      setError(null);

      try {
        // Update each section with new order_index
        const updates = sectionIds.map((id, index) => ({
          id,
          order_index: index,
          updated_at: new Date().toISOString(),
        }));

        // Use upsert to update all at once
        for (const update of updates) {
          const { error: updateError } = await supabase
            .from("challenge_sections")
            .update({ order_index: update.order_index, updated_at: update.updated_at })
            .eq("id", update.id);

          if (updateError) throw updateError;
        }

        return true;
      } catch (err: any) {
        console.error("Error reordering sections:", err);
        setError(err.message || "Kon secties niet herordenen");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [activeProfile?.id]
  );

  return {
    createSection,
    updateSection,
    deleteSection,
    reorderSections,
    isLoading,
    error,
  };
};

export default useOpdrachtSections;
